import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { useEffect, useRef, useState } from "react";
import * as THREE from 'three';
import { Canvas } from "@react-three/fiber";
import { View } from "@react-three/drei";
import { models, sizes } from "../constants";
import { animateWithGsapTimeline } from "../utils/animations";
import ModelView from "./ModelView";
import { yellowImg } from "../utils";

const Model = () => {
  const [size, setSize] = useState('small');
  const [model, setModel] = useState({
    title: 'iPhone 15 Pro in Natural Titanium',
    color: ['#8F8A81', '#FFE7B9', '#6F6C64'],
    img: yellowImg,
  });
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Fixed image paths - make sure these match your actual folder structure
  const showcaseImages = [
    "/assets/images/1.jpg", // Removed "public" prefix as it's not needed in paths
    "/assets/images/16.jpg",
    "/assets/images/iphone.jpg",
    "/assets/images/chip.jpeg",
    "/assets/images/pro.jpg",
  ];

  // Feature titles for each image
  const featureTitles = [
    "Pro Camera System",
    "Super Retina XDR Display",
    "A17 Pro Chip",
    "Titanium Design",
    "All-day Battery Life"
  ];

  // camera control for the model view
  const cameraControlSmall = useRef();
  const cameraControlLarge = useRef();

  // model
  const small = useRef(new THREE.Group());
  const large = useRef(new THREE.Group());

  // rotation
  const [smallRotation, setSmallRotation] = useState(0);
  const [largeRotation, setLargeRotation] = useState(0);

  const tl = gsap.timeline();

  // Auto slider for bubbles
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => 
        prevIndex === showcaseImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 3000);
    
    return () => clearInterval(interval);
  }, [showcaseImages.length]);

  useEffect(() => {
    // Animate the bubble highlight
    gsap.to(".bubble", { 
      opacity: 0.5,
      scale: 1,
      duration: 0.3
    });
    
    gsap.to(`.bubble-${currentImageIndex}`, { 
      opacity: 1,
      scale: 1.2,
      duration: 0.3
    });
  }, [currentImageIndex]);

  useEffect(() => {
    if (size === 'large') {
      animateWithGsapTimeline(tl, small, smallRotation, '#view1', '#view2', {
        transform: 'translateX(-100%)',
        duration: 2
      });
    }

    if (size === 'small') {
      animateWithGsapTimeline(tl, large, largeRotation, '#view2', '#view1', {
        transform: 'translateX(0)',
        duration: 2
      });
    }
  }, [size]);

  useGSAP(() => {
    gsap.to('#heading', { y: 0, opacity: 1, duration: 1 });
    gsap.to('#subheading', { opacity: 1, delay: 0.5, duration: 1 });
    gsap.to('#model-container', { opacity: 1, y: 0, duration: 1, delay: 0.3 });
    gsap.to('#controls', { opacity: 1, y: 0, duration: 1, delay: 0.6 });
    gsap.to('#bubble-container', { opacity: 1, y: 0, duration: 1, delay: 0.8 });
    
    // Initial animation for bubbles
    gsap.to(`.bubble-${currentImageIndex}`, { 
      opacity: 1,
      scale: 1.2,
      duration: 0.3
    });
  }, []);

  const handleBubbleClick = (index) => {
    setCurrentImageIndex(index);
  };

  return (
    <section className="bg-black text-white min-h-screen py-12 px-4 sm:py-16 md:py-20">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 md:mb-12">
          <h1 
            id="heading" 
            className="text-3xl sm:text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent opacity-0 transform translate-y-10"
          >
            iPhone 15 Pro
          </h1>
          <p 
            id="subheading" 
            className="text-gray-400 mt-3 max-w-xl mx-auto opacity-0 text-sm sm:text-base"
          >
            Titanium. So strong. So light. So Pro.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
          {/* 3D Model Viewer - 7 columns on large screens */}
          <div className="lg:col-span-7">
            <div 
              id="model-container" 
              className="relative w-full h-[50vh] sm:h-[55vh] md:h-[60vh] lg:h-[70vh] rounded-xl sm:rounded-2xl overflow-hidden opacity-0 transform translate-y-10 border border-gray-800 bg-gray-900/50 backdrop-blur-sm"
            >
              <ModelView
                index={1}
                groupRef={small}
                gsapType="view1"
                controlRef={cameraControlSmall}
                setRotationState={setSmallRotation}
                item={model}
                size={size}
              />

              <ModelView
                index={2}
                groupRef={large}
                gsapType="view2"
                controlRef={cameraControlLarge}
                setRotationState={setLargeRotation}
                item={model}
                size={size}
              />

              <Canvas
                className="w-full h-full"
                style={{
                  position: 'fixed',
                  top: 0,
                  bottom: 0,
                  left: 0,
                  right: 0,
                  overflow: 'hidden'
                }}
                eventSource={document.getElementById('root')}
              >
                <View.Port />
              </Canvas>
            </div>
          </div>
          
          {/* Right Side - 5 columns on large screens */}
          <div className="lg:col-span-5 flex flex-col space-y-4 sm:space-y-6">
            {/* Image Slider */}
            <div 
              id="bubble-container" 
              className="relative w-full h-[30vh] sm:h-[32vh] md:h-[35vh] rounded-xl sm:rounded-2xl overflow-hidden opacity-0 transform translate-y-10 border border-gray-800 bg-gray-900/50 backdrop-blur-sm"
            >
              {/* Image slider */}
              <div className="w-full h-full relative">
                {showcaseImages.map((img, index) => (
                  <div 
                    key={index}
                    className={`absolute inset-0 transition-opacity duration-700 ${
                      index === currentImageIndex ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    {/* Use your actual image path - the placeholder is just for demo */}
                    <img 
                      src={img} 
                      alt={`iPhone feature ${index + 1}`}
                      className="w-full h-full object-cover" 
                      onError={(e) => {
                        // Fallback if image doesn't load
                        e.target.onerror = null;
                        e.target.src = "/api/placeholder/800/600";
                      }}
                    />
                  </div>
                ))}
                
                {/* Feature title based on current image */}
                <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-black to-transparent p-4 sm:p-6">
                  <h3 className="text-white text-lg sm:text-xl font-medium">
                    {featureTitles[currentImageIndex]}
                  </h3>
                </div>
                
                {/* Bubble navigation */}
                <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2 sm:gap-3">
                  {showcaseImages.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => handleBubbleClick(index)}
                      className={`bubble bubble-${index} w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-300 opacity-50 ${
                        index === currentImageIndex ? 'bg-blue-500' : 'bg-white'
                      }`}
                      aria-label={`View feature ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
            
            {/* Controls */}
            <div 
              id="controls" 
              className="bg-gray-900/50 backdrop-blur-sm p-4 sm:p-6 rounded-xl sm:rounded-2xl border border-gray-800 shadow-xl opacity-0 transform translate-y-10 flex-grow"
            >
              <p className="text-center text-base sm:text-lg font-medium text-white mb-4 sm:mb-6">{model.title}</p>

              <div className="space-y-5 sm:space-y-6">
                {/* Color Selection */}
                <div>
                  <p className="text-gray-400 text-xs sm:text-sm mb-2 sm:mb-3">Choose Color</p>
                  <div className="flex justify-center">
                    {models.map((item, i) => (
                      <button
                        key={i}
                        className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full mx-1 sm:mx-2 cursor-pointer transition-all duration-300 hover:scale-110 ${
                          model.title === item.title 
                            ? 'ring-2 ring-blue-500 ring-offset-2 ring-offset-gray-900 scale-110' 
                            : ''
                        }`}
                        style={{ backgroundColor: item.color[0] }}
                        onClick={() => setModel(item)}
                        aria-label={`Select ${item.title}`}
                      />
                    ))}
                  </div>
                </div>

                {/* Size Selection */}
                <div>
                  <p className="text-gray-400 text-xs sm:text-sm mb-2 sm:mb-3">Choose Size</p>
                  <div className="flex justify-center bg-gray-800/70 rounded-lg p-1">
                    {sizes.map(({ label, value }) => (
                      <button
                        key={label}
                        className={`flex-1 py-2 rounded-md transition-all duration-300 text-xs sm:text-sm ${
                          size === value 
                            ? 'bg-blue-600 text-white font-medium shadow-lg' 
                            : 'text-gray-300 hover:bg-gray-700/70'
                        }`}
                        onClick={() => setSize(value)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* CTA Button */}
                <div className="pt-2 sm:pt-4">
                  <button className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 transition-all px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-white font-medium text-sm sm:text-base shadow-lg">
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Model;